import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Sparkles, Check, Loader2 } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";

export default function Newsletter() {
    const { t } = useT();
    const [form, setForm] = useState({ first_name: "", email: "", phone: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.first_name.trim()) return toast.error(t("nl.err_name"));
        if (!form.email.trim() && !form.phone.trim()) return toast.error(t("nl.err_contact"));
        setSubmitting(true);
        try {
            await api.post("/newsletter", {
                first_name: form.first_name.trim(),
                email: form.email.trim() || null,
                phone: form.phone.trim(),
            });
            setDone(true);
            toast.success(t("nl.toast_success"));
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("nl.err_generic"));
        } finally { setSubmitting(false); }
    };

    return (
        <section id="infolettre" data-testid="newsletter-section" className="py-20 md:py-24 scroll-mt-24">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
                <div className="rounded-3xl bg-[#3D8C95] text-white p-8 md:p-12 relative overflow-hidden">
                    <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
                    <div className="absolute -bottom-20 -left-16 w-72 h-72 rounded-full bg-[#E07A5F]/20 blur-3xl" />
                    <div className="relative">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur mb-5">
                            <Sparkles size={13} />
                            <span className="text-[10px] uppercase tracking-[0.25em]">{t("nl.eyebrow")}</span>
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light leading-tight max-w-2xl">
                            {t("nl.title1")}
                            <span className="block italic text-[#F5B79F]">{t("nl.title2")}</span>
                        </h2>
                        <p className="mt-4 text-white/85 text-sm md:text-base max-w-xl leading-relaxed">{t("nl.desc")}</p>
                        {done ? (
                            <div data-testid="newsletter-success" className="mt-8 flex items-center gap-3 text-white">
                                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"><Check size={20} /></div>
                                <div>
                                    <div className="font-display text-xl">{t("nl.thanks", { name: form.first_name })}</div>
                                    <div className="text-xs text-white/75">{t("nl.registered")}</div>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="newsletter-form">
                                <div className="sm:col-span-2 lg:col-span-1">
                                    <Label htmlFor="nl-name" className="text-[10px] uppercase tracking-[0.2em] text-white/80">{t("nl.first_name")}</Label>
                                    <Input id="nl-name" data-testid="nl-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Marie" className="mt-2 rounded-full bg-white/95 border-0 text-[#2C2A29] focus-visible:ring-2 focus-visible:ring-white" required />
                                </div>
                                <div>
                                    <Label htmlFor="nl-email" className="text-[10px] uppercase tracking-[0.2em] text-white/80">{t("nl.email")}</Label>
                                    <Input id="nl-email" type="email" data-testid="nl-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="vous@exemple.com" className="mt-2 rounded-full bg-white/95 border-0 text-[#2C2A29] focus-visible:ring-2 focus-visible:ring-white" />
                                </div>
                                <div>
                                    <Label htmlFor="nl-phone" className="text-[10px] uppercase tracking-[0.2em] text-white/80">{t("nl.or_phone")}</Label>
                                    <Input id="nl-phone" data-testid="nl-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="514-555-1234" className="mt-2 rounded-full bg-white/95 border-0 text-[#2C2A29] focus-visible:ring-2 focus-visible:ring-white" />
                                </div>
                                <div className="flex items-end">
                                    <Button type="submit" data-testid="nl-submit" disabled={submitting} className="w-full rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white py-6 border-0 shadow-lg">
                                        {submitting ? <Loader2 size={16} className="animate-spin" /> : (<><Mail size={16} className="mr-2" /> {t("nl.submit")}</>)}
                                    </Button>
                                </div>
                                <p className="col-span-full text-[11px] text-white/70">{t("nl.note")}</p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
